import { useQuery } from '@tanstack/react-query'
import { Activity, Server, HardDrive, Film, Cpu, MemoryStick, Clock } from 'lucide-react'
import { system } from '../api/client'

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const mins = Math.floor((seconds % 3600) / 60)

  const parts = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (mins > 0) parts.push(`${mins}m`)

  return parts.join(' ') || '< 1m'
}

function formatBytes(gb: number): string {
  if (gb >= 1000) {
    return `${(gb / 1000).toFixed(2)} TB`
  }
  return `${gb.toFixed(2)} GB`
}

function ProgressBar({ percent, color = 'primary' }: { percent: number; color?: string }) {
  const colorClass = {
    primary: 'bg-primary-500',
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
  }[color] || 'bg-primary-500'

  return (
    <div className="h-2 bg-dark-700 rounded-full overflow-hidden">
      <div
        className={`h-full ${colorClass} transition-all duration-300`}
        style={{ width: `${Math.min(100, percent)}%` }}
      />
    </div>
  )
}

function getUsageColor(percent: number): string {
  if (percent < 60) return 'green'
  if (percent < 85) return 'yellow'
  return 'red'
}

export default function Status() {
  const { data: status, isLoading, error } = useQuery({
    queryKey: ['system-status'],
    queryFn: () => system.getStatus(),
    refetchInterval: 5000, // Refresh every 5 seconds
  })

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6">
        <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <Activity size={24} />
          System Status
        </h1>
        <div className="text-dark-400">Loading...</div>
      </div>
    )
  }

  if (error || !status) {
    return (
      <div className="p-4 sm:p-6">
        <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <Activity size={24} />
          System Status
        </h1>
        <div className="text-red-400">Failed to load system status</div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <Activity size={24} />
        System Status
      </h1>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {/* Active Transcodes */}
        <div className="card">
          <div className="flex items-center gap-2 text-dark-400 mb-2">
            <Film size={18} />
            <span className="text-sm">Transcodes</span>
          </div>
          <p className="text-2xl font-bold text-primary-400">{status.active_transcodes ?? 0}</p>
        </div>

        {/* CPU Usage */}
        <div className="card">
          <div className="flex items-center gap-2 text-dark-400 mb-2">
            <Cpu size={18} />
            <span className="text-sm">CPU</span>
          </div>
          <p className="text-2xl font-bold">{(status.cpu_usage ?? 0).toFixed(1)}%</p>
        </div>

        {/* Memory Usage */}
        <div className="card">
          <div className="flex items-center gap-2 text-dark-400 mb-2">
            <MemoryStick size={18} />
            <span className="text-sm">Memory</span>
          </div>
          <p className="text-2xl font-bold">{(status.memory_percent ?? 0).toFixed(1)}%</p>
        </div>

        {/* Uptime */}
        <div className="card">
          <div className="flex items-center gap-2 text-dark-400 mb-2">
            <Clock size={18} />
            <span className="text-sm">Uptime</span>
          </div>
          <p className="text-2xl font-bold">{formatUptime(status.uptime_seconds ?? 0)}</p>
        </div>
      </div>

      {/* Active Transcodes */}
      {(status.transcodes?.length ?? 0) > 0 && (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Film size={20} />
            Active Transcodes
          </h2>
          <div className="space-y-3">
            {status.transcodes.map((transcode) => (
              <div key={transcode.id} className="flex items-center justify-between bg-dark-700 rounded-lg p-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{transcode.file_name}</p>
                  <div className="flex items-center gap-3 text-sm text-dark-400">
                    <span className="bg-primary-600/20 text-primary-400 px-2 py-0.5 rounded">
                      {transcode.quality}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {formatUptime(transcode.elapsed_seconds)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* System Info */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Server size={20} />
          System Information
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <p className="text-dark-400 text-sm">Hostname</p>
            <p className="font-medium">{status.hostname}</p>
          </div>
          <div>
            <p className="text-dark-400 text-sm">Operating System</p>
            <p className="font-medium">{status.os_name} {status.os_version}</p>
          </div>
        </div>
      </div>

      {/* Memory Details */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <MemoryStick size={20} />
          Memory
        </h2>
        <div className="mb-2">
          <div className="flex justify-between text-sm mb-1">
            <span>{formatBytes(status.memory_used_gb ?? 0)} used</span>
            <span className="text-dark-400">{formatBytes(status.memory_total_gb ?? 0)} total</span>
          </div>
          <ProgressBar percent={status.memory_percent ?? 0} color={getUsageColor(status.memory_percent ?? 0)} />
        </div>
      </div>

      {/* Storage */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <HardDrive size={20} />
          Storage
        </h2>
        <div className="space-y-4">
          {(status.disks ?? []).map((disk, index) => (
            <div key={index}>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium">{disk.mount_point}</span>
                <span className="text-dark-400 text-xs">{disk.name}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span>{formatBytes(disk.used_gb ?? 0)} used</span>
                <span className="text-dark-400">{formatBytes(disk.total_gb ?? 0)} total</span>
              </div>
              <ProgressBar percent={disk.percent_used ?? 0} color={getUsageColor(disk.percent_used ?? 0)} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
